'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { api } from '@/lib/api';
import { Prompt } from '@/types';
import { useAuth } from '@/lib/auth-context';
import { useT } from '@/lib/i18n';
import { extractVariables, segmentPrompt, substituteVariables } from '@/lib/prompt-vars';
import { useAIModels } from '@/lib/ai-models-context';
import PromptToolbar from '@/components/prompt/PromptToolbar';
import AdSlot from '@/components/ads/AdSlot';
import { useToast } from '@/lib/toast';
import { copyToClipboard } from '@/lib/clipboard';
import SavePopover from '@/components/prompt/SavePopover';
import ForkTree from '@/components/prompt/ForkTree';
import ShareButton from '@/components/ShareButton';
import ReportButton from '@/components/prompt/ReportButton';
import CommentThread from '@/components/prompt/CommentThread';
import BotBadge from '@/components/common/BotBadge';

const CATEGORY_GRADIENTS: Record<string, string> = {
  Writing:   'from-violet-900 via-indigo-900 to-slate-950',
  Coding:    'from-blue-900 via-cyan-900 to-slate-950',
  Art:       'from-fuchsia-900 via-pink-900 to-slate-950',
  Business:  'from-emerald-900 via-teal-900 to-slate-950',
  Education: 'from-amber-900 via-orange-900 to-slate-950',
  Other:     'from-slate-800 via-slate-900 to-slate-950',
};

export default function PromptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const t = useT();
  const toast = useToast();
  const { models: aiModels } = useAIModels();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const [copyCount, setCopyCount] = useState(0);
  const [copyBusy, setCopyBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  // Translation state — null = original. When set, the prompt block renders
  // the translated text (variable substitution is disabled while translated
  // since the bracket syntax doesn't survive translation).
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [translatedLang, setTranslatedLang] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    api.prompts.get(id)
      .then((p) => {
        setPrompt(p);
        setIsSaved(p.is_saved);
        setSaveCount(p.save_count);
        setCopyCount(p.copy_count ?? 0);
        setActiveImage(0);
      })
      .catch(() => setError(t('prompt_detail.not_found')))
      .finally(() => setLoading(false));
  }, [id]);

  const variables = useMemo(
    () => (prompt ? extractVariables(prompt.content) : []),
    [prompt]
  );
  const filledContent = useMemo(
    () => (prompt ? substituteVariables(prompt.content, varValues) : ''),
    [prompt, varValues]
  );
  const aiModelById = useMemo(() => new Map(aiModels.map((m) => [m.id, m])), [aiModels]);
  const filledCount = variables.filter((v) => (varValues[v] ?? '').trim() !== '').length;

  const handleCopy = async () => {
    if (!prompt) return;
    if (copyBusy) return; // brief debounce — stops double-clicks double-counting
    setCopyBusy(true);
    setTimeout(() => setCopyBusy(false), 800);
    // Copy whatever's visible: translated text wins, then variable-substituted,
    // then raw original. Keeps "what you see is what you copy" intuitive.
    const visible = translatedText ?? (filledContent || prompt.content);
    const ok = await copyToClipboard(visible);
    if (!ok) {
      toast.error(t('prompt_detail.clipboard_error'));
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    const filled = variables.length > 0 && filledCount > 0;
    toast.success(filled ? t('prompt_detail.copied_with_values').replace('{count}', String(filledCount)) : t('prompt_detail.copied_to_clipboard'));
    // Optimistic bump; server response reconciles (no-op if throttled).
    setCopyCount((c) => c + 1);
    api.prompts.copy(prompt.id)
      .then((r) => {
        if (typeof r?.copy_count === 'number') setCopyCount(r.copy_count);
      })
      .catch(() => { /* ignore */ });
  };

  const handleSaveToggle = async () => {
    if (!user) { router.push('/auth/login'); return; }
    if (saving) return;
    setSaving(true);
    try {
      if (isSaved) {
        await api.prompts.unsave(id);
        setIsSaved(false);
        setSaveCount((c) => Math.max(0, c - 1));
        toast.info(t('prompt_detail.removed_from_collection'));
      } else {
        await api.prompts.save(id);
        setIsSaved(true);
        setSaveCount((c) => c + 1);
        toast.success(t('prompt_detail.saved_to_collection'));
      }
    } catch {
      toast.error(t('common.error_generic'));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="px-6 lg:px-12 py-10 animate-pulse">
        <div className="max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-surface-container-high rounded-xl" />
          <div className="space-y-4">
            <div className="h-3 w-24 bg-surface-container-high rounded" />
            <div className="h-10 bg-surface-container-high rounded w-3/4" />
            <div className="h-4 bg-surface-container-high rounded w-full" />
            <div className="h-4 bg-surface-container-high rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-20 text-center">
        <span className="material-symbols-outlined text-6xl text-outline-variant">search_off</span>
        <p className="text-on-surface-variant text-lg mt-4">{error || t('prompt_detail.not_found')}</p>
        <Link href="/" className="mt-6 inline-block text-primary hover:underline">← {t('prompt_detail.back_to_gallery')}</Link>
      </div>
    );
  }

  const gradient = CATEGORY_GRADIENTS[prompt.category || 'Other'] || CATEGORY_GRADIENTS.Other;
  const isOwner = user?.id === prompt.user_id;
  const imageUrls = Array.from(
    new Set(
      [...(prompt.images ?? []), prompt.cover_image_url]
        .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
        .map((url) => url.trim()),
    ),
  );
  const safeActiveImage = Math.min(activeImage, Math.max(0, imageUrls.length - 1));

  return (
    <div>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-10">
        {/* Back */}
        <button onClick={() => router.back()}
          className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors mb-10">
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
          {t('prompt_detail.back_to_gallery')}
        </button>

        <div className="max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Cover */}
          <div className="space-y-3">
            <div className="relative aspect-square rounded-xl overflow-hidden bg-surface-container-high">
              {imageUrls.length > 0 ? (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={imageUrls[safeActiveImage]}
                    alt={imageUrls.length > 1 ? `${prompt.title} ${safeActiveImage + 1}/${imageUrls.length}` : prompt.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    referrerPolicy="no-referrer"
                  />
                  {imageUrls.length > 1 && (
                    <>
                      <button
                        type="button"
                        aria-label="Previous image"
                        onClick={() => setActiveImage((i) => (i - 1 + imageUrls.length) % imageUrls.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/45 text-white backdrop-blur flex items-center justify-center hover:bg-black/60 transition-colors"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>chevron_left</span>
                      </button>
                      <button
                        type="button"
                        aria-label="Next image"
                        onClick={() => setActiveImage((i) => (i + 1) % imageUrls.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/45 text-white backdrop-blur flex items-center justify-center hover:bg-black/60 transition-colors"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>chevron_right</span>
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-black/45 px-3 py-2 backdrop-blur">
                        {imageUrls.map((url, idx) => (
                          <button
                            key={`${url}-${idx}`}
                            type="button"
                            aria-label={`Show image ${idx + 1}`}
                            aria-current={idx === safeActiveImage ? 'true' : undefined}
                            onClick={() => setActiveImage(idx)}
                            className={`h-2 rounded-full transition-all ${idx === safeActiveImage ? 'w-5 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'}`}
                          />
                        ))}
                      </div>
                      <div className="absolute top-4 right-4 rounded-full bg-black/45 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                        {safeActiveImage + 1}/{imageUrls.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className={`relative w-full h-full bg-gradient-to-br ${gradient}`}>
                  {/* Decorative quote mark */}
                  <div className="absolute top-4 left-6 text-white/15 font-headline text-8xl leading-none select-none pointer-events-none">
                    &ldquo;
                  </div>
                  {/* Large title as typographic poster */}
                  <div className="absolute inset-0 flex items-center justify-center p-10">
                    <h2 className="font-headline text-white/90 text-3xl lg:text-4xl leading-tight text-center">
                      {prompt.title}
                    </h2>
                  </div>
                  {/* Subtle corner vignette */}
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.35)_100%)] pointer-events-none" />
                </div>
              )}
              {prompt.category && (
                <div className="absolute bottom-4 left-4">
                  <span className="bg-black/50 backdrop-blur text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                    {prompt.category}
                  </span>
                </div>
              )}
            </div>

            {imageUrls.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Prompt image previews">
                {imageUrls.map((url, idx) => (
                  <button
                    key={`${url}-thumb-${idx}`}
                    type="button"
                    aria-label={`Show image ${idx + 1}`}
                    aria-current={idx === safeActiveImage ? 'true' : undefined}
                    onClick={() => setActiveImage(idx)}
                    className={`relative h-20 w-20 flex-none overflow-hidden rounded-lg border-2 bg-surface-container-high transition-all ${
                      idx === safeActiveImage
                        ? 'border-primary shadow-[0_0_0_2px_color-mix(in_oklch,var(--primary)_18%,transparent)]'
                        : 'border-transparent opacity-70 hover:opacity-100 hover:border-outline-variant'
                    }`}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={url}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                    <span className="absolute right-1 top-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      {idx + 1}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Details */}
          <div className="flex flex-col">
            {/* Forked-from lineage */}
            {prompt.forked_from && (
              <Link href={`/prompt/${prompt.forked_from.id}`}
                className="group/fork inline-flex items-center gap-2 self-start mb-4 px-3 py-1.5 rounded-full bg-primary-fixed/50 text-primary text-xs font-medium hover:bg-primary-fixed transition-colors">
                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>fork_right</span>
                <span>
                  {t('prompt_detail.remix_of')} <b className="font-semibold">{prompt.forked_from.title}</b>
                  {' '}{t('prompt_detail.by')}{' '}
                  <span className="underline underline-offset-2 decoration-primary/40 group-hover/fork:decoration-primary">@{prompt.forked_from.user.username}</span>
                </span>
              </Link>
            )}

            {/* Category label */}
            {prompt.category && (
              <span className="font-label text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold mb-3">
                {prompt.category}
              </span>
            )}

            <h1 className="font-headline text-4xl lg:text-5xl text-on-surface leading-tight mb-4">
              {prompt.title}
            </h1>

            {/* Author */}
            <Link href={`/profile/${prompt.user.username}`}
              className="group/author flex items-center gap-3 mb-6 w-fit hover:-translate-y-px transition-transform"
              title={t('prompt_detail.view_profile_of').replace('{name}', prompt.user.name)}>
              {prompt.user.avatar_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={prompt.user.avatar_url} alt={prompt.user.name}
                  className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary-fixed flex items-center justify-center text-sm font-bold text-primary">
                  {prompt.user.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-semibold text-on-surface text-sm group-hover/author:text-primary transition-colors">
                  {prompt.user.name}
                  {prompt.user.is_system && <> <BotBadge /></>}
                </p>
                <p className="text-xs text-on-surface-variant">@{prompt.user.username}</p>
              </div>
            </Link>

            {/* Stats */}
            <div
              className="stat-row mb-6"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                background: 'var(--bg-soft)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}
            >
              <StatCell icon="bookmark" label={t('prompt_detail.stat_saves')} value={saveCount} delta={saveCount > 0 ? 'active' : undefined} />
              <StatCell icon="content_copy" label={t('prompt_detail.stat_copies')} value={copyCount} divider delta={copyCount > 0 ? 'active' : undefined} />
              <StatCell icon="visibility" label={t('prompt_detail.stat_views')} value={prompt.view_count} divider />
            </div>

            {/* Description */}
            {prompt.description && (
              <p className="text-on-surface-variant leading-relaxed mb-6">{prompt.description}</p>
            )}

            {/* AI models — same data the modal renders, mirrored on the
                detail page so the canonical page is feature-parity with the
                quick-look modal. Each chip is clickable: navigates to home
                with `?search=ModelName` so visitors can discover other
                prompts that work with the same model. */}
            {prompt.models && prompt.models.length > 0 && (
              <div className="mb-6">
                <span className="font-label text-[10px] uppercase tracking-[0.18em] text-on-surface-variant font-semibold block mb-2">
                  {t('prompt_detail.works_with')}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {prompt.models.map((id) => {
                    const m = aiModelById.get(id);
                    const label = m?.name ?? id;
                    return (
                      <Link
                        key={id}
                        href={`/models/${id}`}
                        title={t('prompt_detail.open_model_details').replace('{name}', label)}
                        className="no-underline inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-transform hover:-translate-y-0.5"
                        style={{
                          background: m ? `${m.color}18` : 'var(--bg-soft)',
                          color: m?.color ?? 'var(--ink-2)',
                          border: `1px solid ${m?.color ?? 'var(--line)'}33`,
                        }}
                      >
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ background: m?.color ?? 'var(--ink-3)' }}
                        />
                        {label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tags */}
            {prompt.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {prompt.tags.map((tag) => (
                  <Link
                    key={tag}
                    href={`/?tags=${encodeURIComponent(tag)}`}
                    className="no-underline bg-primary-fixed text-primary text-xs px-3 py-1 rounded-full font-medium hover:opacity-80 transition-opacity"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mt-auto">
              <button onClick={handleCopy}
                disabled={copyBusy}
                aria-busy={copyBusy || undefined}
                className={`flex-1 min-w-[180px] flex items-center justify-center gap-2 py-3.5 rounded-full font-medium text-sm transition-all ${
                  copied ? 'bg-emerald-600 text-white' : 'bg-gradient-to-br from-primary to-primary-container text-white hover:opacity-90 active:scale-95'
                }`}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{copied ? 'check' : 'content_copy'}</span>
                {copied ? t('prompt_detail.copied') : (copyBusy ? t('prompt_detail.copying') : t('prompt_detail.copy_prompt'))}
              </button>
              {user ? (
                <SavePopover
                  promptId={prompt.id}
                  initialSaved={isSaved}
                  onSavedChange={(s) => { setIsSaved(s); setSaveCount((c) => s ? c + 1 : Math.max(0, c - 1)); }}
                  variant="pill"
                  align="left"
                />
              ) : (
                <Link href="/auth/login"
                  className="flex items-center justify-center gap-2 py-3.5 px-6 rounded-full border border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary transition-colors font-medium text-sm">
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>bookmark</span>
                  {t('prompt_detail.sign_in_to_save')}
                </Link>
              )}
              {!isOwner && (
                <Link href={user ? `/create?fork=${prompt.id}` : '/auth/login'}
                  className="flex items-center gap-2 px-5 py-3.5 rounded-full border border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary transition-colors text-sm font-medium"
                  title={t('prompt_detail.remix_tooltip')}>
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>fork_right</span>
                  {t('prompt_detail.remix')}
                </Link>
              )}
              {isOwner && (
                <Link href={`/edit/${prompt.id}`}
                  className="flex items-center justify-center w-12 h-12 rounded-full border border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary transition-colors">
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit</span>
                </Link>
              )}
              {/* Share — opens Twitter/Facebook intent URLs or copies the link.
                  Facebook and X both consume the OG tags our SEO layer ships,
                  so the embedded preview has the prompt title + cover image. */}
              <ShareButton
                url={`/prompt/${prompt.id}`}
                title={`${prompt.title} — via Prompt Save`}
                iconOnly
                className="flex items-center justify-center w-12 h-12 rounded-full border border-outline-variant text-on-surface-variant hover:border-primary hover:text-primary transition-colors"
              />
              {/* Report — only shown to authed users (auth-aware inside) */}
              <ReportButton promptId={prompt.id} />
            </div>
          </div>
        </div>

        {/* Customize — interactive variables */}
        {variables.length > 0 && (
          <div className="max-w-5xl mt-12">
            <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
              <div>
                <span className="font-label text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold block mb-1">
                  {t('prompt_detail.customize')}
                </span>
                <h2 className="font-headline text-2xl text-on-surface">{t('prompt_detail.fill_blanks')}</h2>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5 text-on-surface-variant">
                  <span className={`w-1.5 h-1.5 rounded-full ${filledCount === variables.length ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  <b className="text-on-surface">{filledCount}</b>/{variables.length} {t('prompt_detail.filled')}
                </span>
                {filledCount > 0 && (
                  <button onClick={() => setVarValues({})}
                    className="text-on-surface-variant hover:text-primary inline-flex items-center gap-1">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>restart_alt</span>
                    {t('prompt_detail.reset')}
                  </button>
                )}
              </div>
            </div>
            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {variables.map((v) => (
                <label key={v} className="block">
                  <span className="font-label text-[10px] uppercase tracking-widest text-on-surface-variant block mb-1.5">
                    {v.replace(/_/g, ' ').toLowerCase()}
                  </span>
                  <input
                    id={`var-input-${v}`}
                    value={varValues[v] ?? ''}
                    onChange={(e) => setVarValues((s) => ({ ...s, [v]: e.target.value }))}
                    placeholder={`[${v}]`}
                    className="w-full px-4 py-2.5 bg-surface-container-low border border-outline-variant rounded-xl text-sm placeholder:text-outline font-mono focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  />
                </label>
              ))}
            </div>
            <p className="text-xs text-outline mt-3">
              {t('prompt_detail.tip_leave_blank_pre')} <code className="bg-surface-container-low px-1.5 py-0.5 rounded font-mono text-[11px]">[TOKEN]</code> {t('prompt_detail.tip_leave_blank_post')}
            </p>
          </div>
        )}

        {/* Prompt content */}
        <div className="max-w-5xl mt-12">
          <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="font-headline text-2xl text-on-surface">{t('prompt_detail.the_prompt')}</h2>
              {variables.length > 0 && filledCount > 0 && (
                <span className="text-xs text-on-surface-variant inline-flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '14px' }}>check_circle</span>
                  {filledCount === variables.length ? t('prompt_detail.ready_to_copy') : t('prompt_detail.preview_with_values')}
                </span>
              )}
            </div>
            {/* Translate + token toolbar. We translate the SUBSTITUTED text
                so variables get baked in first. While translated, the
                interactive variable pills are hidden (they don't survive
                translation), matching expectations. */}
            <PromptToolbar
              text={translatedText ?? substituteVariables(prompt.content, varValues)}
              activeLang={translatedLang}
              onTranslated={(translated, lang) => {
                setTranslatedText(translated);
                setTranslatedLang(translated ? (lang ?? null) : null);
              }}
            />
          </div>
          <div className="bg-slate-900 rounded-xl p-6 relative group">
            <button onClick={handleCopy}
              disabled={copyBusy}
              aria-busy={copyBusy || undefined}
              className="absolute top-4 right-4 flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5 rounded-full transition-colors">
              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>{copied ? 'check' : 'content_copy'}</span>
              {copied ? t('prompt_detail.copied_short') : (copyBusy ? t('prompt_detail.copying') : t('prompt_detail.copy'))}
            </button>
            {translatedText ? (
              // While translated: render the translation as a flat block
              // (variable pills are pre-translation; rendering them here
              // would mix two languages awkwardly).
              <pre className="text-emerald-400 font-mono text-sm leading-relaxed whitespace-pre-wrap break-words pr-20">
                {translatedText}
              </pre>
            ) : variables.length > 0 ? (
              // Interactive render — tokens become live pills that show the
              // user's value in accent color or a dashed-outline placeholder
              // when empty. Click a pill → scroll the matching input into view.
              <pre className="text-emerald-400 font-mono text-sm leading-relaxed whitespace-pre-wrap break-words pr-20">
                {segmentPrompt(prompt.content).map((seg, i) => {
                  if (seg.kind === 'text') return <span key={i}>{seg.text}</span>;
                  const val = (varValues[seg.name] ?? '').trim();
                  const filled = val.length > 0;
                  return (
                    <span
                      key={i}
                      onClick={() => {
                        // Scroll + focus the matching input for quick iteration.
                        const el = document.getElementById(`var-input-${seg.name}`) as HTMLInputElement | null;
                        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        el?.focus();
                      }}
                      style={{
                        display: 'inline-block',
                        padding: '0 6px',
                        margin: '0 1px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-mono)',
                        border: filled ? '1px solid transparent' : '1px dashed rgba(255,255,255,0.35)',
                        background: filled ? 'color-mix(in oklch, var(--accent) 35%, transparent)' : 'transparent',
                        color: filled ? '#ffffff' : 'rgba(255,255,255,0.55)',
                        fontWeight: filled ? 500 : 400,
                        transition: 'all 0.15s',
                      }}
                      title={filled ? `${seg.name} = ${val}` : t('prompt_detail.click_to_fill').replace('{name}', seg.name)}
                    >
                      {filled ? val : `[${seg.name}]`}
                    </span>
                  );
                })}
              </pre>
            ) : (
              <pre className="text-emerald-400 font-mono text-sm leading-relaxed whitespace-pre-wrap break-words pr-20">
                {prompt.content}
              </pre>
            )}
          </div>

          {/* Sponsored slot — between the prompt and the lineage tree.
              Below-content ads tend to perform better than above-content
              and don't push the prompt itself off-screen. */}
          <div style={{ marginTop: 36, maxWidth: 920 }}>
            <AdSlot slot="prompt-detail-below" format="rectangle" />
          </div>

          {/* Lineage — fork ancestors + descendants. Collapsed-friendly
              section below the prompt body. */}
          <div style={{ marginTop: 48 }}>
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6,
            }}>
              {t('prompt_detail.lineage')}
            </div>
            <h2 style={{
              fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 600,
              letterSpacing: '-0.02em', color: 'var(--ink)', margin: '0 0 12px',
            }}>
              {t('prompt_detail.fork_tree')}
            </h2>
            <ForkTree promptId={prompt.id} />
          </div>

          {/* Public discussion thread — single-level comments. */}
          <div className="max-w-5xl mt-12">
            <CommentThread promptId={prompt.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCell({
  icon, label, value, divider, delta,
}: {
  icon: string;
  label: string;
  value: number;
  divider?: boolean;
  delta?: 'active';
}) {
  return (
    <div
      style={{
        padding: '14px 18px',
        borderLeft: divider ? '1px solid var(--line)' : 'none',
        transition: 'background 0.15s',
      }}
      className="group/stat"
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          color: delta === 'active' ? 'var(--accent)' : 'var(--ink-3)',
          marginBottom: 8,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{icon}</span>
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: 600,
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 26,
          fontWeight: 600,
          letterSpacing: '-0.03em',
          lineHeight: 1,
          color: 'var(--ink)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}
